import { Injectable, Signal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RuntimeConfigService } from '../common/runtime-config.service';
import type { ApiResponse } from '../../../types/commons';
import type { FilingSessionLimits } from '../../../types/api-response';

/**
 * Client-side stand-in for the server's upload limits, used ONLY when
 * `GET /filing-sessions/limits` could not be reached.
 *
 * Developer Note (accepted risk, 2026-07-10): this constant duplicates values the
 * backend owns via `GET /filing-sessions/limits`, and the hand-off doc explicitly
 * warns against hardcoding them — a stale copy here would silently mis-size the
 * picker label and pre-check exactly as `fileSizeValidationMode` once did.
 *
 * We take the risk deliberately: these values change at most once (upcoming `.doc`
 * support adds to `allowedExtensions`), and the constant is only ever read when the
 * `/limits` fetch has failed. Three guardrails keep it from going silent:
 *   1. Every fallback read emits a `console.warn`.
 *   2. `isServerProvided()` is `false` while it is in use, so the 413 path defers to
 *      the server's own `errors` string rather than quoting a cap we guessed.
 *   3. A failed fetch retries on the next uploader mount.
 *
 * If a fourth value ever needs adding here, that is the signal the fallback has
 * outgrown its justification — fetch-or-fail instead.
 */
export const FALLBACK_FILING_SESSION_LIMITS: FilingSessionLimits = {
  maxFileSizeBytes: 50_000_000,
  maxFileCount: 10,
  allowedExtensions: ['.docx', '.pdf'],
};

/**
 * Owns `GET /filing-sessions/limits`: fetches it once per app lifetime, caches
 * the result, and seats `FALLBACK_FILING_SESSION_LIMITS` until (or unless) the
 * server answers.
 *
 * Why its own `HttpClient` rather than a method on `FormSubmitService`, which
 * owns the sibling `filing-sessions/*` routes: `FilingSessionUploader` needs the
 * cap to compose its 413 copy, so `FormSubmitService` would have to depend on
 * this service, which would depend back on it. The duplicated URL prefix buys us
 * out of that cycle.
 *
 * The route is auth-guarded. `ensureLoaded()` is therefore called from the
 * uploader component's `ngOnInit` (which sits behind `isAuthenticatedGuard`),
 * never from `APP_INITIALIZER` — a boot-time fetch would 401 on the login page.
 *
 * On a 401, `sessionExpiryInterceptor` runs UPSTREAM of this service's error
 * handler: it forces the logout and re-throws. We cannot suppress that, and must
 * not try. Both paths run — the user is redirected, and we seat the fallback and
 * warn into a page that is already unmounting. Harmless.
 */
@Injectable({ providedIn: 'root' })
export class FilingSessionLimitsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  private readonly limitsSignal = signal<FilingSessionLimits>(
    FALLBACK_FILING_SESSION_LIMITS,
  );
  private readonly isServerProvidedSignal = signal<boolean>(false);

  /** Never null. Seeded with the fallback so templates always render a cap. */
  readonly limits: Signal<FilingSessionLimits> = this.limitsSignal.asReadonly();

  /**
   * `false` while the fallback is in use. Gates the 413 copy: when we are
   * guessing the cap we must render the server's own `errors` string rather
   * than composing a sentence around a number the server never enforced.
   */
  readonly isServerProvided: Signal<boolean> = this.isServerProvidedSignal.asReadonly();

  private inFlight = false;

  /**
   * Idempotent. Fires the GET once per app lifetime. A failed attempt is retried
   * on the next call (i.e. the next uploader mount); a successful one is never
   * refetched — limits are global, not user-scoped, so they survive a logout.
   */
  ensureLoaded(): void {
    if (this.isServerProvidedSignal() || this.inFlight) {
      return;
    }
    this.inFlight = true;

    const url = `${this.config.apiUrl()}/${this.config.apiVersion()}/mbrs/filing-sessions/limits`;

    this.http
      .get<ApiResponse<FilingSessionLimits>>(url, { withCredentials: true })
      .subscribe({
        next: (response) => {
          this.inFlight = false;
          const parsed = parseLimits(response?.data);
          if (!parsed) {
            this.warnFallback('the response payload was malformed');
            return;
          }
          this.limitsSignal.set(parsed);
          this.isServerProvidedSignal.set(true);
        },
        error: (err: unknown) => {
          this.inFlight = false;
          this.warnFallback(
            `the request failed (${err instanceof Error ? err.message : String(err)})`,
          );
        },
      });
  }

  private warnFallback(reason: string): void {
    // eslint-disable-next-line no-console
    console.warn(
      `[filing-session-limits] Falling back to hardcoded upload limits because ${reason}. ` +
        `Cap ${FALLBACK_FILING_SESSION_LIMITS.maxFileSizeBytes} bytes, ` +
        `${FALLBACK_FILING_SESSION_LIMITS.maxFileCount} files, ` +
        `${FALLBACK_FILING_SESSION_LIMITS.allowedExtensions.join(', ')}. ` +
        `These may be stale — the server is authoritative.`,
    );
  }
}

/**
 * Validates the wire payload before we trust it. A partially-populated response
 * is worse than no response: it would seat `isServerProvided = true` around a
 * bogus cap, disabling the fallback's own guardrails.
 *
 * `allowedExtensions` is de-duplicated (case-insensitively, first spelling wins,
 * order preserved). Observed 2026-07-10: the backend returned
 * `[".docx", ".pdf", ".docx", ".pdf"]`, which would render the picker hint as
 * "Supported format: *.docx, *.pdf, *.docx, *.pdf" and emit a repeated `accept`
 * attribute. Duplicates are semantically harmless to `isFileExtensionValid`, so
 * this is a display concern only — but the display is the whole point of the
 * payload. Reported to the backend; this normalisation stays regardless, because
 * a validated seam should not hand un-normalised data to a template.
 */
function parseLimits(data: unknown): FilingSessionLimits | null {
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const raw = data as Record<string, unknown>;

  const maxFileSizeBytes = raw['maxFileSizeBytes'];
  const maxFileCount = raw['maxFileCount'];
  const allowedExtensions = raw['allowedExtensions'];

  if (typeof maxFileSizeBytes !== 'number' || !Number.isFinite(maxFileSizeBytes) || maxFileSizeBytes <= 0) {
    return null;
  }
  if (typeof maxFileCount !== 'number' || !Number.isInteger(maxFileCount) || maxFileCount <= 0) {
    return null;
  }
  if (
    !Array.isArray(allowedExtensions) ||
    allowedExtensions.length === 0 ||
    !allowedExtensions.every((extension) => typeof extension === 'string' && extension.startsWith('.'))
  ) {
    return null;
  }

  const seen = new Set<string>();
  const dedupedExtensions = (allowedExtensions as string[]).filter((ext) => {
    const key = ext.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return {
    maxFileSizeBytes,
    maxFileCount,
    allowedExtensions: dedupedExtensions,
  };
}
